import React, { useContext, useRef, useState } from 'react';
import { Box } from 'theme-ui';
import Icon from '@solid-ui-components/ContentIcon';
import ContentContainer from '@solid-ui-components/ContentContainer';
import { ModalContext } from '@solid-ui-components/Modal';
import { TabsContext } from '@solid-ui-components/Tabs';
import { buildLinkProps } from '@solid-ui-components/ContentButtons';
import style from './style.js';
import useOutsideClick from '@solid-ui-components/utils/useOnClickOutside';
import { isClassElement } from '@solid-ui-components/utils/isClassElement';
import FullButton from '@solid-ui-components/FullButton';
import ArrowIcon from '@solid-ui-components/Icons/ArrowIcon';

const buttonsWithInnerContent = [
  'Product',
  'Industries',
  'Resources',
  'Продукт',
  'Применение',
  'Ресурсы'
];

const ButtonComponent = ({
  content,
  children,
  styles = {},
  className,
  onClick,
}) => {
  const { setActiveModal } = useContext(ModalContext);
  const { setActiveTab } = useContext(TabsContext);

  const { type, text, subtext, invSubtext, link, target, variant, width, bg, icon } =
    content;

  const { Component, linkProps } = buildLinkProps({
    content: { type, link, target, variant },
    setActiveModal,
    setActiveTab,
  });

  return (
    <Component
      as={'div'}
      variant={variant || 'primary'}
      sx={{
        width,
        '::after': { bg, borderColor: bg },
        position: 'relative',
        ...styles,
      }}
      {...linkProps}
      className={[linkProps.className, className].join(' ')}
      onClick={onClick}
    >
      <Box sx={{ display: subtext ? 'flex' : invSubtext ? 'flex' : 'inline-block', gap: '8px' }}>
        {subtext ? (
          <>
            <Icon content={icon} size='xs'  />
            <Box sx={style.buttonText}>
              <span>{text}</span>
              <Box sx={style.subtext}>{subtext}</Box>
            </Box>
          </>
        ) : invSubtext ? (
          <>
            <Icon content={icon} size='xs' sx={{alignContent: 'center'}}/>
            <Box sx={style.buttonText}>
              <span>{text}</span>
            </Box>
          </>
        ) : (
          <>
            {text}
            <Icon content={icon} size='xs' mb='1' ml='12px' />
          </>
        )}
      </Box>
      {children}
    </Component>
  );
};

const ContentButton = ({ content, level = 1 }) => {
  if (content.type === 'FULL') {
    return <FullButton content={content} />;
  }
  const { buttons } = content;
  const collection = content.collection;
  const className = `level-${level}`;

  const [isMenuOpened, setIsMenuOpened] = useState(false);

  if (!collection && !buttons)
    return <ButtonComponent content={content} className={className} />;

  const ButtonGroup = ({ buttons }) =>
    buttons.map((content, index) => (
      <ContentButton
        key={`item-${index}`}
        level={level + 1}
        content={content}
      />
    ));
  const parentButtonClass = buttonsWithInnerContent.includes(content.text)
    ? content.text
    : '';
  const subButtons = useRef(null);
  const arrowRef = useRef(null);

  useOutsideClick(
    subButtons,
    () => {
      subButtons?.current?.classList?.remove('SubButtonsOpened');
      if (isMenuOpened) setIsMenuOpened(false);
    },
    parentButtonClass
  );

  const onClick = (event) => {
    if (
      isClassElement(event?.target, parentButtonClass) ||
      isClassElement(event?.currentTarget, parentButtonClass)
    ) {
      arrowRef?.current?.classList?.toggle('SubButtonsOpenedArrow');
      subButtons?.current?.classList?.toggle('SubButtonsOpened');
      setIsMenuOpened(!isMenuOpened);
    }
  };

  return (
    <ButtonComponent
      content={content}
      styles={{
        ...style.subButtonsParent,
        textDecoration: [
          'none',
          null,
          null,
          null,
          isMenuOpened ? 'underline' : 'none',
        ],
      }}
      className={`${className} ${parentButtonClass}`}
      onClick={onClick}
    >
      {level === 1 && (
        <>
          {parentButtonClass ? (
            <Box
              sx={style.arrowWrapper}
              ref={arrowRef}
              className={parentButtonClass}
            >
              {
                <ArrowIcon
                  style={{
                    ...style.arrow,
                    transform: isMenuOpened
                      ? 'rotate(90deg)'
                      : 'rotate(-90deg)',
                  }}
                />
              }
            </Box>
          ) : (
            <ArrowIcon style={style.arrow} />
          )}
        </>
      )}
      <Box
        sx={{
          ...style.subContainer,
          ...(parentButtonClass ? style.subContainerCollectionMobile : {}),
        }}
        className={`container-level-${level} ${
          !collection ? 'no-collection' : ''
        } ${parentButtonClass}`}
        ref={parentButtonClass ? subButtons : undefined}
      >
        {collection ? (
          collection.map(({ container, buttons }, index) => {
            return (
              <ContentContainer
                key={`item-${index}`}
                content={container}
                sx={style.subContainerCollection}
              >
                <ButtonGroup buttons={buttons} />
              </ContentContainer>
            );
          })
        ) : (
          <ButtonGroup buttons={buttons} />
        )}
      </Box>
    </ButtonComponent>
  );
};

const ContentButtons = ({ content, variant, wrapperStyles }) =>
  content ? (
    <>
      <Box sx={{ ...style[variant], ...wrapperStyles }}>
        {content?.map((content, index) => (
          <ContentButton
            key={`item-${index}`}
            index={index}
            content={content}
          />
        ))}
      </Box>
    </>
  ) : null;

export default ContentButtons;

ContentButtons.defaultProps = {
  variant: 'horizontal',
};
